import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import KPICard from "@/components/KPICard";
import { api } from "@/App";
import { toast } from "sonner";
import { 
  FileCheck, 
  Loader2,
  Download,
  Search
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function HRDashboard() {
  const [stats, setStats] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    try {
      const params = { status: "signed" };
      if (selectedMonth && selectedMonth !== "all") params.month = parseInt(selectedMonth);
      if (selectedYear) params.year = parseInt(selectedYear);

      const [statsRes, objectivesRes] = await Promise.all([
        api.get("/statistics/dashboard"),
        api.get("/objectives", { params })
      ]);
      setStats(statsRes.data);
      setObjectives(objectivesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const monthNames = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  const filteredObjectives = objectives.filter(obj => 
    obj.seller_name?.toLowerCase().includes(search.toLowerCase()) ||
    obj.concession_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <Layout title="Ressources Humaines">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#E30613]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Documents signés - Ressources Humaines">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-testid="hr-dashboard-kpis">
        <KPICard
          value={stats?.objectives_signed || 0}
          label="Objectifs signés"
          icon={FileCheck}
          color="green"
        />
        <KPICard
          value={stats?.objectives_sent_to_hr || 0}
          label="Envoyés aux RH"
          icon={Download}
          color="blue"
        />
      </div>

      {/* Filters */}
      <div className="card-dashboard mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Rechercher par vendeur ou concession..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="hr-search-input"
            />
          </div>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-40" data-testid="hr-month-filter">
              <SelectValue placeholder="Tous les mois" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les mois</SelectItem>
              {monthNames.slice(1).map((month, index) => (
                <SelectItem key={index + 1} value={(index + 1).toString()}>
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-32" data-testid="hr-year-filter">
              <SelectValue placeholder="Année" />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Documents Table */}
      <div className="card-dashboard" data-testid="hr-documents-table">
        {filteredObjectives.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendeur</TableHead>
                <TableHead>Concession</TableHead>
                <TableHead>Période</TableHead>
                <TableHead className="text-center">VN</TableHead>
                <TableHead className="text-center">VO</TableHead>
                <TableHead className="text-center">Financement</TableHead>
                <TableHead>Signé le</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredObjectives.map((obj) => (
                <TableRow key={obj.id}>
                  <TableCell className="font-medium">{obj.seller_name}</TableCell>
                  <TableCell>{obj.concession_name}</TableCell>
                  <TableCell>{monthNames[obj.month]} {obj.year}</TableCell>
                  <TableCell className="text-center">{obj.vn_target}</TableCell>
                  <TableCell className="text-center">{obj.vo_target}</TableCell>
                  <TableCell className="text-center">{obj.financing_target?.toLocaleString()}€</TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {obj.manager_signed_at ? new Date(obj.manager_signed_at).toLocaleDateString('fr-FR') : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="empty-state">
            <FileCheck className="empty-state-icon" />
            <p>Aucun document signé trouvé</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
